import type {
  IsolationMatrixSpec,
  MatrixValue,
  ResolvedFigure,
  ShapeRole,
} from '../model.js';
import {
  box,
  finishFigure,
  point,
  SceneBuilder,
  validateViewport,
} from '../scene-builder.js';

const STATUS: Record<MatrixValue, {
  glyph: string;
  compact: string;
  label: string;
  role: ShapeRole;
}> = {
  allowed: { glyph: '×', compact: 'CAN OCCUR', label: 'anomaly can occur', role: 'negative' },
  prevented: { glyph: '✓', compact: 'BLOCKED', label: 'anomaly is prevented', role: 'positive' },
  depends: { glyph: '~', compact: 'ENGINE', label: 'depends on the database engine', role: 'conditional' },
};

export function isolationCellId(rowId: string, columnId: string): string {
  return `matrix.cell.${rowId}.${columnId}`;
}

/**
 * Compiles semantic isolation guarantees into a fixed typographic grid.
 * The source spec contains no coordinates; all geometry follows from the
 * viewport, row/column counts, and the table view's density contract.
 */
export function compileIsolationMatrix(spec: IsolationMatrixSpec): ResolvedFigure {
  validateIsolationMatrix(spec);

  const builder = new SceneBuilder();
  const { width, height } = spec.viewport;
  const narrow = width <= 390;
  const margin = width <= 340 ? 10 : Math.max(14, Math.min(28, width * 0.045));
  const tableX = margin;
  const tableY = narrow ? 48 : 52;
  const tableWidth = width - margin * 2;
  const rowHeaderWidth = narrow
    ? Math.max(92, Math.min(112, tableWidth * 0.32))
    : Math.max(148, Math.min(176, tableWidth * 0.3));
  const cellWidth = (tableWidth - rowHeaderWidth) / spec.columns.length;
  const columnHeaderHeight = narrow ? 68 : 76;
  const footerHeight = narrow ? 62 : 66;
  const availableRowsHeight = height - tableY - columnHeaderHeight - footerHeight;
  const rowHeight = Math.max(38, Math.min(narrow ? 68 : 72, availableRowsHeight / spec.rows.length));
  const tableHeight = columnHeaderHeight + rowHeight * spec.rows.length;
  const tableBottom = tableY + tableHeight;
  const warnings: string[] = [];

  builder.rect('matrix.frame', 'matrix', 'structure', box(tableX, tableY, tableWidth, tableHeight), { radius: 2 });
  builder.rect('matrix.corner', 'matrix.corner', 'header', box(tableX, tableY, rowHeaderWidth, columnHeaderHeight));
  builder.text('matrix.corner.eyebrow', 'matrix.corner', 'annotation', point(tableX + 12, tableY + 21), 'ISOLATION', {
    size: narrow ? 9 : 10,
    mono: true,
    weight: 700,
    uppercase: true,
  });
  builder.text('matrix.corner.title', 'matrix.corner', 'structure', point(tableX + 12, tableY + 43), narrow ? 'LEVEL' : 'GUARANTEE LEVEL', {
    size: narrow ? 12 : 13,
    mono: true,
    weight: 700,
  });
  builder.text('matrix.corner.axis', 'matrix.corner', 'annotation', point(tableX + 12, tableY + 59), narrow ? 'vs anomaly →' : 'compared with anomaly →', {
    size: narrow ? 8 : 9,
    mono: true,
  });

  spec.columns.forEach((column, columnIndex) => {
    const semanticId = `matrix.column.${column.id}`;
    const x = tableX + rowHeaderWidth + columnIndex * cellWidth;
    const bounds = box(x, tableY, cellWidth, columnHeaderHeight);
    builder.rect(`${semanticId}.surface`, semanticId, 'header', bounds, { label: column.label, interactive: true });

    const sourceLabel = narrow ? column.shortLabel : column.label;
    const fontSize = narrow ? Math.max(7, Math.min(9, cellWidth / 6.5)) : Math.max(9, Math.min(11, cellWidth / 8));
    const maxChars = Math.max(4, Math.floor((cellWidth - 10) / (fontSize * 0.61)));
    const lines = fitLabel(sourceLabel, maxChars, narrow ? 2 : 3);
    const lineGap = fontSize + 3;
    const firstY = tableY + columnHeaderHeight / 2 - ((lines.length - 1) * lineGap) / 2 + fontSize * 0.34;
    lines.forEach((line, lineIndex) => {
      builder.text(`${semanticId}.label.${lineIndex}`, semanticId, 'structure', point(x + cellWidth / 2, firstY + lineIndex * lineGap), line, {
        size: fontSize,
        mono: true,
        weight: 700,
        align: 'middle',
        uppercase: true,
      });
    });
    builder.anchor(`${semanticId}.bottom`, semanticId, point(x + cellWidth / 2, tableY + columnHeaderHeight), 'down');
    builder.hit(semanticId, bounds);
  });

  spec.rows.forEach((row, rowIndex) => {
    const rowSemanticId = `matrix.row.${row.id}`;
    const y = tableY + columnHeaderHeight + rowIndex * rowHeight;
    const rowBounds = box(tableX, y, rowHeaderWidth, rowHeight);
    builder.rect(`${rowSemanticId}.surface`, rowSemanticId, 'surface', rowBounds, { label: `${row.label}: ${row.description}`, interactive: true });

    const rowFontSize = narrow ? 9 : 10;
    const maxRowChars = Math.max(9, Math.floor((rowHeaderWidth - 20) / (rowFontSize * 0.61)));
    const rowLines = fitLabel(row.label, maxRowChars, narrow ? 2 : 3);
    const rowLineGap = rowFontSize + 3;
    const rowFirstY = y + rowHeight / 2 - ((rowLines.length - 1) * rowLineGap) / 2 + rowFontSize * 0.34;
    rowLines.forEach((line, lineIndex) => {
      builder.text(`${rowSemanticId}.label.${lineIndex}`, rowSemanticId, 'structure', point(tableX + 11, rowFirstY + lineIndex * rowLineGap), line, {
        size: rowFontSize,
        mono: true,
        weight: 700,
        uppercase: true,
      });
    });
    builder.anchor(`${rowSemanticId}.right`, rowSemanticId, point(tableX + rowHeaderWidth, y + rowHeight / 2), 'right');
    builder.hit(rowSemanticId, rowBounds);

    spec.columns.forEach((column, columnIndex) => {
      const value = row.values[column.id]!;
      const status = STATUS[value];
      const semanticId = isolationCellId(row.id, column.id);
      const x = tableX + rowHeaderWidth + columnIndex * cellWidth;
      const bounds = box(x, y, cellWidth, rowHeight);
      const accessibleLabel = `${row.label} × ${column.label}: ${status.label}`;

      builder.rect(`${semanticId}.surface`, semanticId, status.role, bounds, { label: accessibleLabel, interactive: true });
      builder.text(`${semanticId}.glyph`, semanticId, status.role, point(x + cellWidth / 2, y + rowHeight / 2 + (width >= 520 ? -2 : 6)), status.glyph, {
        size: narrow ? 18 : 20,
        weight: 700,
        align: 'middle',
      });
      if (width >= 520) {
        builder.text(`${semanticId}.status`, semanticId, 'annotation', point(x + cellWidth / 2, y + rowHeight / 2 + 17), status.compact, {
          size: Math.max(7, Math.min(9, cellWidth / 11)),
          mono: true,
          weight: 700,
          align: 'middle',
          uppercase: true,
        });
      }
      builder.anchor(`${semanticId}.center`, semanticId, point(x + cellWidth / 2, y + rowHeight / 2));
      builder.hit(semanticId, bounds);
      builder.relation(
        `guarantee.${row.id}.${column.id}`,
        value,
        rowSemanticId,
        `matrix.column.${column.id}`,
        accessibleLabel,
      );
    });
  });

  const legend = [
    { value: 'prevented' as const, title: narrow ? 'BLOCKED' : 'PREVENTED' },
    { value: 'allowed' as const, title: narrow ? 'CAN OCCUR' : 'CAN OCCUR' },
    { value: 'depends' as const, title: narrow ? 'ENGINE' : 'ENGINE-SPECIFIC' },
  ];
  const legendGap = narrow ? 5 : 9;
  const legendWidth = (tableWidth - legendGap * 2) / 3;
  const legendY = tableBottom + 13;
  legend.forEach((item, index) => {
    const status = STATUS[item.value];
    const semanticId = `matrix.legend.${item.value}`;
    const x = tableX + index * (legendWidth + legendGap);
    builder.rect(`${semanticId}.surface`, semanticId, status.role, box(x, legendY, legendWidth, 30), { radius: 15, label: status.label });
    builder.text(`${semanticId}.glyph`, semanticId, status.role, point(x + (narrow ? 13 : 16), legendY + 20), status.glyph, {
      size: 14,
      weight: 700,
      align: 'middle',
    });
    builder.text(`${semanticId}.label`, semanticId, 'structure', point(x + (narrow ? 25 : 29), legendY + 19), fitInline(item.title, narrow ? Math.max(5, Math.floor((legendWidth - 29) / 5.1)) : 18), {
      size: narrow ? 7 : 8,
      mono: true,
      weight: 700,
      uppercase: true,
    });
  });

  const expectedCellCount = spec.rows.length * spec.columns.length;
  if (narrow) warnings.push('Matrix cells use the compact-symbol label policy at this width.');
  if (rowHeight < 46) warnings.push('Matrix rows are approaching the table view density floor.');
  if (expectedCellCount > 24) warnings.push('Matrix exceeds the preferred 24-cell learning density.');

  const active = spec.focusCellId ? [spec.focusCellId] : ['matrix'];
  active.push(...(spec.emphasis ?? []));

  return finishFigure(spec, builder, {
    title: 'Isolation is a guarantee against specific anomalies',
    subtitle: 'Read across one row: stronger levels prevent more invalid histories.',
    state: spec.focusCellId ? `focus:${spec.focusCellId}` : 'overview',
    active,
    warnings,
  });
}

export function validateIsolationMatrix(spec: IsolationMatrixSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.columns.length < 2 || spec.columns.length > 5) {
    throw new Error('isolation-matrix: require 2…5 anomaly columns');
  }
  if (spec.rows.length < 2 || spec.rows.length > 6) {
    throw new Error('isolation-matrix: require 2…6 isolation rows');
  }
  if (!spec.provenance.trim()) throw new Error('isolation-matrix: provenance is required');

  assertUnique(spec.columns.map((column) => column.id), 'column');
  assertUnique(spec.rows.map((row) => row.id), 'row');

  const columnIds = new Set(spec.columns.map((column) => column.id));
  for (const column of spec.columns) {
    if (!validSemanticId(column.id)) throw new Error(`isolation-matrix: invalid column id ${column.id}`);
    if (!column.label.trim() || !column.shortLabel.trim()) throw new Error(`isolation-matrix: column ${column.id} requires labels`);
  }

  const cellIds = new Set<string>();
  for (const row of spec.rows) {
    if (!validSemanticId(row.id)) throw new Error(`isolation-matrix: invalid row id ${row.id}`);
    if (!row.label.trim() || !row.description.trim()) throw new Error(`isolation-matrix: row ${row.id} requires a label and description`);
    const supplied = Object.keys(row.values);
    if (supplied.length !== columnIds.size || supplied.some((id) => !columnIds.has(id))) {
      throw new Error(`isolation-matrix: row ${row.id} must define every anomaly exactly once`);
    }
    for (const column of spec.columns) {
      const value = row.values[column.id];
      if (value !== 'allowed' && value !== 'prevented' && value !== 'depends') {
        throw new Error(`isolation-matrix: invalid value for ${row.id}/${column.id}`);
      }
      cellIds.add(isolationCellId(row.id, column.id));
    }
  }

  if (spec.focusCellId && !cellIds.has(spec.focusCellId)) {
    throw new Error(`isolation-matrix: focus cell ${spec.focusCellId} does not exist`);
  }
}

function assertUnique(ids: string[], kind: 'row' | 'column'): void {
  if (new Set(ids).size !== ids.length) throw new Error(`isolation-matrix: duplicate ${kind} id`);
}

function validSemanticId(id: string): boolean {
  return /^[a-z][a-z0-9-]*$/.test(id);
}

function fitLabel(value: string, maxChars: number, maxLines: number): string[] {
  const words = value.trim().replace(/\s+/g, ' ').split(' ');
  const lines: string[] = [];
  let line = '';

  for (const rawWord of words) {
    const word = fitInline(rawWord, maxChars);
    if (!line) {
      line = word;
    } else if (`${line} ${word}`.length <= maxChars) {
      line += ` ${word}`;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  if (lines.length <= maxLines) return lines;

  const visible = lines.slice(0, maxLines);
  visible[maxLines - 1] = fitInline(lines.slice(maxLines - 1).join(' '), maxChars);
  return visible;
}

function fitInline(value: string, maxChars: number): string {
  if (value.length <= maxChars) return value;
  if (maxChars <= 3) return value.slice(0, maxChars);
  return `${value.slice(0, maxChars - 1)}…`;
}
